import { useCurrentUser } from '@state/user.ts'

import type { StockLevel } from '@state/product.ts'

/**
 * Centralized mapper for handling stock level transformations based on user role.
 *
 * The UNKNOWN stock level is a special state that should only be visible to developers.
 * For non-developer users, UNKNOWN is transformed to HAS_STOCK as a safe fallback.
 */
export class StockLevelMapper {
  public readonly isHidingUnknown: boolean
  private readonly fallbackStockLevel: StockLevel

  constructor(fallbackStockLevel: StockLevel) {
    this.isHidingUnknown = fallbackStockLevel !== 'UNKNOWN'
    this.fallbackStockLevel = fallbackStockLevel
  }

  /**
   * Maps a single stock level value.
   * Transforms UNKNOWN to HAS_STOCK if the user role doesn't allow unknown states.
   */
  map = (stockLevel: StockLevel): StockLevel => {
    return stockLevel === 'UNKNOWN' ? this.fallbackStockLevel : stockLevel
  }

  /**
   * Maps an array of items that contain a stockLevel property.
   * Preserves all other properties while transforming only the stockLevel.
   */
  mapArray = <T extends { stockLevel: StockLevel }>(items: readonly T[]): T[] => {
    return items.map(item => ({
      ...item,
      stockLevel: this.map(item.stockLevel),
    }))
  }
}

/**
 * React hook that provides a StockLevelMapper instance based on the current user's role.
 * Developers are allowed to see UNKNOWN stock levels, while other users are not.
 */
export function useStockLevelMapper(): StockLevelMapper {
  const { role } = useCurrentUser()
  return new StockLevelMapper(role === 'DEVELOPER' ? 'UNKNOWN' : 'HAS_STOCK')
}

